import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { getNeighborhoodById } from '@/API/apiService';
import Picture from '@/image/hotel/picture5.png';

import { FooterItems } from '@/components/common/FooterItems';
import { HotelCard, HotelCardData } from '@/components/Magazine/HotelCard';
import { MagazineParts } from '@/components/hotel/MagazineParts';
import { News } from '@/components/hotel/News';
import { DetileTextImage } from '@/components/common/DetileTextImage';
import { MagazineExample } from '@/components/Magazine/MagazineExample';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

const FALLBACK_IMAGE = Picture;

export const MagazineDetail: React.FC = () => {
  // URLパラメータから magazine_id を取得
  const { magazine_id } = useParams<{ magazine_id: string }>();
  // useLocationで URL の先頭セグメントを取得し、'en'なら英語、それ以外は日本語とする
  const location = useLocation();
  const langSegment = location.pathname.split('/')[1];
  const useEnglish = langSegment === 'en';

  // API取得データ用の state
  const [neighborhoodData, setNeighborhoodData] = useState<any>(null);
  // ローディングフラグ
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchNeighborhoodById = async () => {
      if (!magazine_id) return;
      try {
        setLoading(true);
        const response = await getNeighborhoodById(magazine_id);
        setNeighborhoodData(response.data.data);
      } catch (error) {
        console.error('Failed to fetch neighborhood by ID:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchNeighborhoodById();
  }, [magazine_id]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!neighborhoodData) {
    return <div>No neighborhood data found.</div>;
  }

  // フィールド抽出
  const {
    header_image_url,
    title,
    title_en,
    body,
    body_en,
    date_time,
    images,
    information,
    information_en,
    tags = [],
    properties = [],
  } = neighborhoodData;

  // 日付(YYYY-MM-DD)
  const displayDate = date_time ? date_time.slice(0, 10) : '';

  // 言語に応じた各フィールドの表示内容
  const displayTitle = useEnglish ? title_en : title;
  const displayBody = useEnglish ? body_en : body;
  const displayInfo = useEnglish ? information_en : information;

  // メイン画像: header_image_url があれば使用、なければ FALLBACK_IMAGE
  const mainImage =
    header_image_url && header_image_url.trim() !== ''
      ? header_image_url
      : FALLBACK_IMAGE;

  // タグの表示（各タグはカンマ区切り）
  const tagsContent = tags.length ? (
    <div>
      {tags.map((tag: any, index: number) => (
        <span key={tag.id} className="mr-2">
          {useEnglish ? tag.name_en : tag.name}
          {index < tags.length - 1 && ', '}
        </span>
      ))}
    </div>
  ) : null;

  // HotelCard 用のデータ
  const stayCardData: HotelCardData[] = properties.map((prop: any) => ({
    propertyId: prop.id,
    imageUrl: FALLBACK_IMAGE, // 本来は prop の画像URLを使用
    location: useEnglish ? prop.name_en : prop.name,
    subLocation:
      prop.sub_areas && prop.sub_areas.length > 0
        ? useEnglish
          ? prop.sub_areas.map((sa: any) => sa.name_en).join(', ')
          : prop.sub_areas.map((sa: any) => sa.name).join(', ')
        : '',
    area: prop.area
      ? {
          id: prop.area.id,
          name: prop.area.name,
          name_en: prop.area.name_en,
        }
      : null,
    subAreas: Array.isArray(prop.sub_areas)
      ? prop.sub_areas.map((sa: any) => ({
          id: sa.id,
          name: sa.name,
          name_en: sa.name_en,
        }))
      : [],
  }));

  // meta title の設定（言語に応じて）
  const metaTitle = useEnglish ? `${title_en} | Tokyo` : title;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
      </Helmet>
      <div className="bg-whiteYellow relative w-full">
        <DetileTextImage
          imageSrc={mainImage}
          fallbackImage={Picture}
          bottomText={
            <div className="px-4">
              <div className="text-[10px] mb-8">{displayDate}</div>
              <div className="text-[20px]">{displayTitle || '(No Title)'}</div>
              <div className="text-[10px] underline">{tagsContent || ''}</div>
              <div
                className="py-5 text-base"
                dangerouslySetInnerHTML={{
                  __html: displayBody || '(No Body Content)',
                }}
              />
            </div>
          }
          imageTall={true}
        />

        <div className="py-4">
          <MagazineExample
            images={images}
            fallbackImage={Picture}
            bottomTextPosition="center"
            bottomText={
              <div className="text-base space-y-2">
                <div>{useEnglish ? 'Shop Information' : 'ショップ情報'}</div>
                <div
                  className="pt-8"
                  dangerouslySetInnerHTML={{
                    __html: displayInfo || 'No information available',
                  }}
                />
              </div>
            }
            imageTall={true}
          />
        </div>

        <div className="px-3">
          <div className="text-center">
            <div className="py-4 md:pb-10 mt-20 text-lg">
              (STAY IN NEIGHBORHOOD)
            </div>
            <HotelCard stayCardData={stayCardData} />
          </div>

          <div className="mb-[6.35rem]">
            <MagazineParts />
          </div>

          <FooterItems />
        </div>
      </div>
    </>
  );
};
